import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { callFunction } from '@/lib/api';
import { env } from '@/lib/env';
import { supabase } from '@/lib/supabase';

// ── Types (mirror Communicate MessageSummary / GetThreadOutput) ───────────────

export type CareCommunicationDirection = 'in' | 'out';

export type CareCommunicationStatus =
  | 'received'
  | 'validated'
  | 'queued'
  | 'sent'
  | 'delivered'
  | 'acknowledged'
  | 'failed'
  | 'rejected';

export type CareCommunicationAckType = 'AA' | 'AE' | 'AR';

export type CareCommunicationCategory =
  | 'alcohol-and-drug-treatment'
  | 'assistive-devices'
  | 'carecoordination'
  | 'decease'
  | 'discharge'
  | 'examination-results'
  | 'healthcare'
  | 'home-care-assessment'
  | 'medicine'
  | 'nursing'
  | 'outpatient'
  | 'psychiatry-social-disability'
  | 'regarding-referral'
  | 'telemedicine'
  | 'training'
  | 'acute-ambulant'
  | 'extended-care-responsibility'
  | 'other';

export const CARE_COMMUNICATION_CATEGORIES: CareCommunicationCategory[] = [
  'alcohol-and-drug-treatment',
  'assistive-devices',
  'carecoordination',
  'decease',
  'discharge',
  'examination-results',
  'healthcare',
  'home-care-assessment',
  'medicine',
  'nursing',
  'outpatient',
  'psychiatry-social-disability',
  'regarding-referral',
  'telemedicine',
  'training',
  'acute-ambulant',
  'extended-care-responsibility',
  'other',
];

export const CARE_COMMUNICATION_STATUSES: Array<CareCommunicationStatus | 'all'> = [
  'all',
  'received',
  'validated',
  'queued',
  'sent',
  'delivered',
  'acknowledged',
  'failed',
  'rejected',
];

export const CARE_COMMUNICATION_DIRECTIONS: Array<CareCommunicationDirection | 'all'> = [
  'all',
  'in',
  'out',
];

/** Common MedCom SKS administrative role codes for author signature. */
export const AUTHOR_ROLE_CODES = [
  'psykolog',
  'speciallaege',
  'sygeplejerske',
  'fysioterapeut',
  'ergoterapeut',
  'socialraadgiver',
  'laegesekretaer',
] as const;

export interface AckSummary {
  id: string;
  ackType: CareCommunicationAckType;
  status: string;
  bundleId: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface CareCommunicationListItem {
  id: string;
  threadId: string | null;
  direction: CareCommunicationDirection;
  status: CareCommunicationStatus;
  communicationIdentifier: string;
  bundleId: string | null;
  senderSor: string | null;
  receiverSor: string | null;
  sentAt: string | null;
  receivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  latestAcknowledgement: AckSummary | null;
  acknowledgementCount: number;
  /** List API does not expose patient/topic/category — filled only when known. */
  topic?: string | null;
  category?: string | null;
  /** True when patient identity is present; NEVER expose CPR value. */
  has_cpr?: boolean;
}

export interface ThreadParty {
  organizationId?: string;
  sorId?: string;
  organizationName?: string;
  practitionerName?: string;
  practitionerRoleCode?: string;
  practitionerRoleText?: string;
  phone?: string;
}

export interface ThreadSegment {
  segmentId: string;
  timestamp: string;
  kind: 'string' | 'attachment';
  text?: string;
  attachment?: {
    contentType?: string;
    title?: string;
    size?: number;
    url?: string;
    hash?: string;
    creation?: string;
  };
  sender: ThreadParty;
  recipient: ThreadParty;
  activityCode?: string;
  messageHeaderId?: string;
  isInitiator: boolean;
}

export interface AssembledThread {
  threadId: string;
  categoryCode?: string;
  topic?: string;
  /** Patient may contain CPR in API response — UI must only use has_cpr, never render values. */
  patient?: {
    cpr?: string;
    xEcpr?: string;
    dEcpr?: string;
    family?: string;
    given?: string[];
  };
  initiatorOrganizationId?: string;
  segments: ThreadSegment[];
  latestMessageHeaderId?: string;
}

export interface ThreadMeta {
  id: string;
  communicationIdentifier: string;
  subject: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface CareCommunicationThread {
  communicationIdentifier: string;
  thread: ThreadMeta | null;
  assembled: AssembledThread | null;
  messages: CareCommunicationListItem[];
}

export interface CareCommunicationListFilters {
  status?: CareCommunicationStatus | 'all';
  direction?: CareCommunicationDirection | 'all';
  search?: string;
}

export interface CareCommunicationListResult {
  items: CareCommunicationListItem[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
}

export interface ClinicCommunicateConnection {
  clinic_id: string;
  sor_id: string | null;
  ean: string | null;
  status: string;
}

export type SendMessageType = 'new' | 'reply' | 'forward';

export interface OrganizationPartyInput {
  sorId: string;
  name: string;
  gln?: string;
}

export interface PatientInput {
  cpr?: string;
  xEcpr?: string;
  dEcpr?: string;
  family: string;
  given?: string[];
}

export interface AuthorSignatureInput {
  family: string;
  given: string[];
  roleCode: string;
  phone: string;
}

export type PayloadSegmentInput =
  | { kind: 'string'; content: string; authoredAt?: string }
  | {
      kind: 'attachment';
      attachment: {
        contentType: string;
        data?: string;
        url?: string;
        title?: string;
        creation?: string;
      };
      authoredAt?: string;
    };

/** Body for communicate-send (clinicId + CareCommunication compose payload). */
export interface SendCareCommunicationInput extends Record<string, unknown> {
  clinicId: string;
  type: SendMessageType;
  parent?: {
    messageId?: string;
    communicationIdentifier?: string;
  };
  sender: OrganizationPartyInput;
  receiver: OrganizationPartyInput;
  patient: PatientInput;
  category: CareCommunicationCategory;
  topic?: string;
  priority?: 'routine' | 'asap';
  author: AuthorSignatureInput;
  payloads: PayloadSegmentInput[];
  senderCareTeamName?: string;
  recipient?: {
    family: string;
    given: string[];
    roleText?: string;
    roleCode?: string;
  };
  idempotencyKey?: string;
}

export interface SendCareCommunicationOutput {
  messageId: string;
  threadId: string;
  communicationIdentifier: string;
  status: string;
  transportStatus?: string;
  bundleId?: string | null;
  sendType?: string;
  parentMessageId?: string | null;
  idempotentReplay?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function asListItem(raw: unknown): CareCommunicationListItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || typeof r.communicationIdentifier !== 'string') return null;

  const latest = r.latestAcknowledgement;
  let latestAcknowledgement: AckSummary | null = null;
  if (latest && typeof latest === 'object') {
    const a = latest as Record<string, unknown>;
    if (typeof a.id === 'string' && typeof a.ackType === 'string') {
      latestAcknowledgement = {
        id: a.id,
        ackType: a.ackType as CareCommunicationAckType,
        status: typeof a.status === 'string' ? a.status : '',
        bundleId: typeof a.bundleId === 'string' ? a.bundleId : null,
        errorCode: typeof a.errorCode === 'string' ? a.errorCode : null,
        errorMessage: typeof a.errorMessage === 'string' ? a.errorMessage : null,
        createdAt: typeof a.createdAt === 'string' ? a.createdAt : '',
      };
    }
  }

  return {
    id: r.id,
    threadId: typeof r.threadId === 'string' ? r.threadId : null,
    direction: (r.direction === 'in' || r.direction === 'out' ? r.direction : 'out'),
    status: (typeof r.status === 'string' ? r.status : 'queued') as CareCommunicationStatus,
    communicationIdentifier: r.communicationIdentifier,
    bundleId: typeof r.bundleId === 'string' ? r.bundleId : null,
    senderSor: typeof r.senderSor === 'string' ? r.senderSor : null,
    receiverSor: typeof r.receiverSor === 'string' ? r.receiverSor : null,
    sentAt: typeof r.sentAt === 'string' ? r.sentAt : null,
    receivedAt: typeof r.receivedAt === 'string' ? r.receivedAt : null,
    createdAt: typeof r.createdAt === 'string' ? r.createdAt : '',
    updatedAt: typeof r.updatedAt === 'string' ? r.updatedAt : '',
    latestAcknowledgement,
    acknowledgementCount:
      typeof r.acknowledgementCount === 'number' ? r.acknowledgementCount : 0,
  };
}

/** True when assembled patient has any identity — never returns/logs the value. */
export function patientHasIdentity(
  patient: AssembledThread['patient'] | null | undefined,
): boolean {
  if (!patient) return false;
  return Boolean(patient.cpr || patient.xEcpr || patient.dEcpr);
}

// ── Query keys ────────────────────────────────────────────────────────────────

export const careCommunicationsListQueryKey = (
  clinicId: string | null,
  filters: CareCommunicationListFilters,
) => ['care-communications', 'list', clinicId, filters] as const;

export const careCommunicationThreadQueryKey = (
  clinicId: string | null,
  communicationIdentifier: string | null | undefined,
) => ['care-communications', 'thread', clinicId, communicationIdentifier] as const;

export const clinicCommunicateConnectionQueryKey = (clinicId: string | null) =>
  ['care-communications', 'connection', clinicId] as const;

// ── useCareCommunicationsList ─────────────────────────────────────────────────

export function useCareCommunicationsList(
  clinicId: string | null | undefined,
  filters: CareCommunicationListFilters,
) {
  return useQuery({
    queryKey: careCommunicationsListQueryKey(clinicId ?? null, filters),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<CareCommunicationListResult> => {
      if (!clinicId) {
        return { items: [], pagination: { limit: 50, offset: 0, total: 0 } };
      }

      const body: Record<string, unknown> = {
        clinicId,
        limit: 100,
        offset: 0,
      };
      if (filters.status && filters.status !== 'all') body.status = filters.status;
      if (filters.direction && filters.direction !== 'all') body.direction = filters.direction;

      const result = await callFunction<Record<string, unknown>, CareCommunicationListResult>(
        'communicate-list',
        body,
      );

      let items = (result.items ?? [])
        .map(asListItem)
        .filter((x): x is CareCommunicationListItem => x !== null);

      const search = filters.search?.trim().toLowerCase();
      if (search) {
        items = items.filter((item) => {
          const hay = [
            item.communicationIdentifier,
            item.senderSor,
            item.receiverSor,
            item.status,
            item.latestAcknowledgement?.ackType,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return hay.includes(search);
        });
      }

      return {
        items,
        pagination: result.pagination ?? {
          limit: 100,
          offset: 0,
          total: items.length,
        },
      };
    },
    staleTime: 15_000,
  });
}

// ── useCareCommunicationThread ────────────────────────────────────────────────

export function useCareCommunicationThread(
  clinicId: string | null | undefined,
  communicationIdentifier: string | null | undefined,
) {
  return useQuery({
    queryKey: careCommunicationThreadQueryKey(clinicId ?? null, communicationIdentifier),
    enabled: Boolean(clinicId && communicationIdentifier),
    queryFn: async (): Promise<CareCommunicationThread> => {
      if (!clinicId || !communicationIdentifier) {
        throw new Error('Missing clinic or communication identifier');
      }

      const result = await callFunction<
        Record<string, unknown>,
        {
          communicationIdentifier: string;
          thread: ThreadMeta | null;
          assembled: AssembledThread | null;
          messages: unknown[];
        }
      >('communicate-thread', {
        clinicId,
        communicationIdentifier,
      });

      return {
        communicationIdentifier: result.communicationIdentifier,
        thread: result.thread,
        assembled: result.assembled,
        messages: (result.messages ?? [])
          .map(asListItem)
          .filter((x): x is CareCommunicationListItem => x !== null),
      };
    },
    staleTime: 5_000,
  });
}

// ── useClinicCommunicateConnection ────────────────────────────────────────────

export function useClinicCommunicateConnection(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: clinicCommunicateConnectionQueryKey(clinicId ?? null),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<ClinicCommunicateConnection | null> => {
      if (!clinicId) return null;
      // Table may be absent from generated types until migrations are applied.
      const { data, error } = await supabase
        .from('clinic_communicate_connections' as never)
        .select('clinic_id, sor_id, ean, status')
        .eq('clinic_id', clinicId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const row = data as unknown as ClinicCommunicateConnection;
      return {
        clinic_id: row.clinic_id,
        sor_id: row.sor_id,
        ean: row.ean,
        status: row.status,
      };
    },
    staleTime: 60_000,
  });
}

// ── useSendCareCommunication ──────────────────────────────────────────────────

export function useSendCareCommunication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: SendCareCommunicationInput,
    ): Promise<SendCareCommunicationOutput> => {
      return await callFunction<SendCareCommunicationInput, SendCareCommunicationOutput>(
        'communicate-send',
        input,
      );
    },
    onSuccess: (row, vars) => {
      qc.invalidateQueries({ queryKey: ['care-communications', 'list', vars.clinicId] });
      if (row.communicationIdentifier) {
        qc.invalidateQueries({
          queryKey: careCommunicationThreadQueryKey(vars.clinicId, row.communicationIdentifier),
        });
      }
      if (vars.parent?.communicationIdentifier) {
        qc.invalidateQueries({
          queryKey: careCommunicationThreadQueryKey(
            vars.clinicId,
            vars.parent.communicationIdentifier,
          ),
        });
      }
    },
  });
}

// ── useExportCareCommunication ────────────────────────────────────────────────

/**
 * Trigger a FHIR Bundle file download via communicate-export Edge Function.
 * Mirrors useExportPayTransactions (raw GET + Content-Disposition blob download).
 */
export function useExportCareCommunication() {
  return useMutation({
    mutationFn: async (args: {
      clinicId: string;
      messageId: string;
      format: 'json' | 'xml';
    }): Promise<void> => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const qs = new URLSearchParams({
        clinicId: args.clinicId,
        messageId: args.messageId,
        format: args.format,
      });

      const resp = await fetch(
        `${env.VITE_SUPABASE_URL}/functions/v1/communicate-export?${qs.toString()}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${session?.access_token ?? ''}`,
            apikey: env.VITE_SUPABASE_ANON_KEY,
          },
        },
      );

      if (!resp.ok) {
        const json = (await resp.json().catch(() => null)) as {
          error?: { message?: string };
        } | null;
        throw new Error(json?.error?.message ?? `Export failed (${resp.status})`);
      }

      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const cd = resp.headers.get('Content-Disposition') ?? '';
      const match = /filename="?([^";]+)"?/.exec(cd);
      a.download = match?.[1] ?? `${args.messageId}.${args.format}`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });
}
